import { useEffect, useState } from 'react';
import { warrantiesAPI, productsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Plus, Search, ShieldCheck, Calendar as CalendarIcon, Package } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

export const WarrantiesPage = () => {
  const { hasRole } = useAuth();
  const [warranties, setWarranties] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    serial_number: '',
    purchase_date: null
  });
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const openDetails = (w) => {
    setSelectedWarranty(w);
    setDetailsOpen(true);
  };

  const refreshSelected = async (id) => {
    try {
      const res = await warrantiesAPI.getById(id);
      setSelectedWarranty(res.data);
    } catch (e) { /* noop */ }
  };

  const approveWarranty = async () => {
    if (!selectedWarranty) return;
    setActionLoading(true);
    try {
      await warrantiesAPI.update(selectedWarranty.id, { status: 'active' });
      toast.success('Warranty approved and activated');
      await refreshSelected(selectedWarranty.id);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve warranty');
    } finally {
      setActionLoading(false);
    }
  };

  const setExtended = async (apply) => {
    if (!selectedWarranty) return;
    setActionLoading(true);
    try {
      await warrantiesAPI.setExtendedWarranty(selectedWarranty.id, apply);
      toast.success(apply ? 'Extended warranty marked as paid & applied' : 'Extended warranty reverted to standard');
      await refreshSelected(selectedWarranty.id);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update extended warranty');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [warrantiesRes, productsRes] = await Promise.all([
        warrantiesAPI.getAll(),
        productsAPI.getAll()
      ]);
      setWarranties(warrantiesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to fetch warranties');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.purchase_date) {
      toast.error('Please select a purchase date');
      return;
    }

    const submitData = {
      ...formData,
      purchase_date: formData.purchase_date.toISOString()
    };
    
    try {
      await warrantiesAPI.register(submitData);
      toast.success('Warranty registered');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register warranty');
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      serial_number: '',
      purchase_date: null
    });
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Unknown Product';
  };

  const filteredWarranties = warranties.filter(warranty => {
    const matchesSearch = warranty.serial_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || warranty.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getDaysRemaining = (expiryDate) => {
    const days = differenceInDays(new Date(expiryDate), new Date());
    return days;
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    expired: 'bg-red-100 text-red-700',
    claimed: 'bg-amber-100 text-amber-700',
    void: 'bg-slate-100 text-slate-600'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="warranties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Warranties</h1>
          <p className="text-muted-foreground mt-1">Manage product warranties</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="register-warranty-btn">
              <Plus className="h-4 w-4 mr-2" />
              Register Warranty
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Register Warranty</DialogTitle>
              <DialogDescription>
                Register a product warranty
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="product">Product *</Label>
                <Select value={formData.product_id} onValueChange={(v) => handleChange('product_id', v)}>
                  <SelectTrigger data-testid="warranty-product-select">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} ({product.warranty_months} mo warranty)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="serial_number">Serial Number *</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => handleChange('serial_number', e.target.value)}
                  required
                  placeholder="Enter product serial number"
                  data-testid="warranty-serial-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Purchase Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="warranty-date-trigger">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.purchase_date ? format(formData.purchase_date, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.purchase_date}
                      onSelect={(date) => handleChange('purchase_date', date)}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="warranty-submit-btn">
                  Register Warranty
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by serial number..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="warranty-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="warranty-filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending Approval</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="claimed">Claimed</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Serial Number</TableHead>
                <TableHead>Purchase Date</TableHead>
                <TableHead>Expiry Date</TableHead>
                <TableHead>Remaining</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWarranties.length > 0 ? (
                filteredWarranties.map((warranty) => {
                  const daysRemaining = getDaysRemaining(warranty.expiry_date);
                  return (
                    <TableRow key={warranty.id} className="data-table-row cursor-pointer" onClick={() => openDetails(warranty)} data-testid={`warranty-row-${warranty.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-sm bg-emerald-100 flex items-center justify-center">
                            <Package className="h-4 w-4 text-emerald-600" />
                          </div>
                          <span className="font-medium">{warranty.product_name || getProductName(warranty.product_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-slate-100 px-2 py-0.5 rounded">{warranty.serial_number}</code>
                      </TableCell>
                      <TableCell>
                        {format(new Date(warranty.purchase_date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        {format(new Date(warranty.expiry_date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        {warranty.status === 'active' ? (
                          <span className={daysRemaining <= 30 ? 'text-amber-600' : daysRemaining <= 0 ? 'text-red-600' : 'text-emerald-600'}>
                            {daysRemaining > 0 ? `${daysRemaining} days` : 'Expired'}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[warranty.status]}>
                          {warranty.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No warranties found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Warranty Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              {selectedWarranty?.product_name || getProductName(selectedWarranty?.product_id)}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex items-center gap-2">
                <span>Registration details</span>
                {selectedWarranty?.status && (
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusColors[selectedWarranty.status]}`}>
                    {selectedWarranty.status === 'pending' ? 'Pending Approval' : selectedWarranty.status}
                  </span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>

          {selectedWarranty && (
            <div className="space-y-4 text-sm">
              {/* Serial numbers */}
              <div>
                <p className="font-medium text-slate-700 mb-1">Serial Numbers</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div><span className="text-muted-foreground">Unit:</span> <code className="bg-slate-100 px-1 rounded">{selectedWarranty.serial_number || 'N/A'}</code></div>
                  <div><span className="text-muted-foreground">Pump:</span> <code className="bg-slate-100 px-1 rounded">{selectedWarranty.pump_serial_number || 'N/A'}</code></div>
                  <div><span className="text-muted-foreground">Oil/Water:</span> <code className="bg-slate-100 px-1 rounded">{selectedWarranty.oil_water_filtration_serial_number || 'N/A'}</code></div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div><span className="text-muted-foreground">Purchased:</span> {selectedWarranty.purchase_date ? format(new Date(selectedWarranty.purchase_date), 'MMM d, yyyy') : 'N/A'}</div>
                <div><span className="text-muted-foreground">Shipped:</span> {selectedWarranty.shipping_date ? format(new Date(selectedWarranty.shipping_date), 'MMM d, yyyy') : 'N/A'}</div>
                <div><span className="text-muted-foreground">Expires:</span> {selectedWarranty.expiry_date ? format(new Date(selectedWarranty.expiry_date), 'MMM d, yyyy') : 'N/A'}</div>
                <div className="col-span-2 sm:col-span-3"><span className="text-muted-foreground">Where purchased:</span> {selectedWarranty.place_of_purchase || 'N/A'}</div>
              </div>

              {/* Coverage */}
              {selectedWarranty.coverage && (
                <div>
                  <p className="font-medium text-slate-700 mb-1">Coverage</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">Structure → {format(new Date(selectedWarranty.coverage.structure), 'MMM yyyy')}</Badge>
                    <Badge variant="outline">Electronics &amp; Vacuum Pump → {format(new Date(selectedWarranty.coverage.electronics_vacuum_pump), 'MMM yyyy')}</Badge>
                    <Badge variant="outline">Cooling → {format(new Date(selectedWarranty.coverage.cooling), 'MMM yyyy')}</Badge>
                  </div>
                </div>
              )}

              {/* Extended warranty */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <p className="font-medium text-indigo-900">Extended Warranty (paid)</p>
                <p className="text-indigo-700 text-xs mt-0.5">
                  Status: <strong>{selectedWarranty.extended_warranty_status || 'none'}</strong>
                  {selectedWarranty.extended_warranty_requested && selectedWarranty.extended_warranty_status === 'requested' && ' — customer requested, awaiting payment'}
                </p>
                <div className="flex gap-2 mt-2">
                  {selectedWarranty.extended_warranty_status !== 'active' ? (
                    <Button size="sm" disabled={actionLoading} onClick={() => setExtended(true)} data-testid="activate-extended-btn">
                      Mark Paid &amp; Apply (3yr structure / 2yr electrical)
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled={actionLoading} onClick={() => setExtended(false)} data-testid="revert-extended-btn">
                      Revert to Standard
                    </Button>
                  )}
                </div>
              </div>

              {/* Contact / shipping */}
              <div>
                <p className="font-medium text-slate-700 mb-1">Contact &amp; Shipping</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <div><span className="text-muted-foreground">Email:</span> {selectedWarranty.email || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selectedWarranty.phone || 'N/A'}</div>
                  <div className="sm:col-span-2"><span className="text-muted-foreground">Address:</span> {[selectedWarranty.address, selectedWarranty.city, selectedWarranty.province_state, selectedWarranty.country].filter(Boolean).join(', ') || 'N/A'}</div>
                </div>
              </div>

              {/* Proof of purchase */}
              {selectedWarranty.proof_of_purchase_url && (
                <div>
                  <p className="font-medium text-slate-700 mb-1">Proof of Purchase</p>
                  <a href={selectedWarranty.proof_of_purchase_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm">View attached file</a>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedWarranty?.status === 'pending' && (
              <Button onClick={approveWarranty} disabled={actionLoading} data-testid="approve-warranty-btn">
                Approve &amp; Activate
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarrantiesPage;
